import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Building2, Send, Search, Loader2, Check, X, Inbox,
  Mail, MapPin, Calendar, XCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });

const STATUS_COLORS = {
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  accepted: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  declined: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  cancelled: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
};

/**
 * Venue partnerships panel for the Organization dashboard.
 * - Lists outbound (org→venue) + inbound (venue→org) invitations.
 * - Lets the org send a new invitation by searching venues.
 * - Inbound invitations get accept/decline buttons.
 */
export default function VenueInvitesPanel() {
  const [outbound, setOutbound] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/organizations/me/venue-invitations?direction=all`, auth());
      const rows = r.data?.invitations || [];
      setOutbound(rows.filter((i) => i.kind === 'org_invites_venue'));
      setInbound(rows.filter((i) => i.kind === 'venue_invites_org'));
    } catch (_) {
      // Org may not have any invites yet — silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const respond = async (id, decision) => {
    try {
      await axios.post(
        `${API}/marketplace/invitations/${id}/respond`,
        { decision },
        auth(),
      );
      toast.success(`Invitation ${decision}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update invitation');
    }
  };

  const cancel = async (id) => {
    try {
      await axios.post(`${API}/marketplace/invitations/${id}/cancel`, {}, auth());
      toast.success('Invitation cancelled');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="venue-invites-panel">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-indigo-400">
            Venue marketplace
          </p>
          <h2 className="text-xl font-bold mt-1 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-300" /> Venue partnerships
          </h2>
        </div>
        <Button
          onClick={() => setShowInvite(true)}
          className="bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white"
          data-testid="venue-invite-open-btn"
        >
          <Send className="h-4 w-4 mr-2" /> Invite a venue
        </Button>
      </div>

      {/* Outbound */}
      <Card data-testid="venue-invites-outbound">
        <CardContent className="p-0">
          <SectionHeader title="Sent" count={outbound.length} icon={Send} />
          {loading ? (
            <Loader />
          ) : outbound.length === 0 ? (
            <Empty>You haven't reached out to any venues yet.</Empty>
          ) : (
            <div className="divide-y divide-border">
              {outbound.map((inv) => (
                <InvitationRow
                  key={inv.invitation_id}
                  inv={inv}
                  direction="outbound"
                  onCancel={() => cancel(inv.invitation_id)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inbound */}
      <Card data-testid="venue-invites-inbound">
        <CardContent className="p-0">
          <SectionHeader title="Received" count={inbound.length} icon={Inbox} />
          {loading ? (
            <Loader />
          ) : inbound.length === 0 ? (
            <Empty>No venues have invited you yet. Build a public profile and list events to attract them.</Empty>
          ) : (
            <div className="divide-y divide-border">
              {inbound.map((inv) => (
                <InvitationRow
                  key={inv.invitation_id}
                  inv={inv}
                  direction="inbound"
                  onAccept={() => respond(inv.invitation_id, 'accepted')}
                  onDecline={() => respond(inv.invitation_id, 'declined')}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <InviteVenueDialog
        open={showInvite}
        onClose={() => setShowInvite(false)}
        onSent={() => { setShowInvite(false); load(); }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function SectionHeader({ title, count, icon: Icon }) {
  return (
    <div className="px-5 py-3 border-b border-border flex items-center justify-between">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-indigo-300" /> {title}
      </h3>
      <span className="text-xs text-muted-foreground tabular-nums">{count}</span>
    </div>
  );
}

function Loader() {
  return (
    <div className="py-8 flex justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
    </div>
  );
}

function Empty({ children }) {
  return (
    <p className="py-8 text-center text-sm text-muted-foreground px-4">{children}</p>
  );
}

function InvitationRow({ inv, direction, onAccept, onDecline, onCancel }) {
  const [venueName, setVenueName] = useState(null);

  // Fetch the venue name lazily so we show "Acme Hall" instead of an id.
  useEffect(() => {
    let cancelled = false;
    if (!inv.venue_id) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/venues/browse?limit=1&venue_id=${inv.venue_id}`);
        // browse doesn't accept id filter — fall back to the detail endpoint
        // if we have to. For now use the heuristic and let it show the id.
        if (!cancelled) {
          const v = (r.data?.venues || []).find((x) => x.venue_id === inv.venue_id);
          if (v) setVenueName(v.name);
        }
      } catch (_) { /* show id */ }
    })();
    return () => { cancelled = true; };
  }, [inv.venue_id]);

  return (
    <div
      className="px-5 py-3 flex flex-wrap items-start justify-between gap-3"
      data-testid={`invite-row-${inv.invitation_id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-sm truncate">
            {venueName || inv.venue_id || 'Venue'}
          </p>
          <Badge className={`${STATUS_COLORS[inv.status] || ''} text-[10px] border`}>
            {inv.status}
          </Badge>
          {inv.proposed_date && (
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {inv.proposed_date}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{inv.message}</p>
        {inv.response_message && (
          <p className="text-[11px] text-emerald-300/80 italic mt-1 line-clamp-1">
            Reply: {inv.response_message}
          </p>
        )}
        <p className="text-[10px] text-muted-foreground/70 mt-1">
          {inv.created_at ? new Date(inv.created_at).toLocaleString() : ''}
        </p>
      </div>
      {direction === 'outbound' && inv.status === 'pending' && (
        <Button
          variant="outline" size="sm"
          onClick={onCancel}
          data-testid={`invite-cancel-${inv.invitation_id}`}
        >
          <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
        </Button>
      )}
      {direction === 'inbound' && inv.status === 'pending' && (
        <div className="flex gap-1">
          <Button
            size="sm"
            onClick={onAccept}
            className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950"
            data-testid={`invite-accept-${inv.invitation_id}`}
          >
            <Check className="h-3.5 w-3.5 mr-1" /> Accept
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={onDecline}
            data-testid={`invite-decline-${inv.invitation_id}`}
          >
            <X className="h-3.5 w-3.5 mr-1" /> Decline
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invite-a-venue dialog with type-ahead venue picker
// ---------------------------------------------------------------------------

function InviteVenueDialog({ open, onClose, onSent }) {
  const [query, setQuery] = useState('');
  const [venues, setVenues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Debounced venue search via /api/venues/browse
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/venues/browse`, { params: { limit: 8, city: query || undefined } });
        const rows = r.data?.venues || [];
        const filtered = query
          ? rows.filter((v) => (v.name || '').toLowerCase().includes(query.toLowerCase()))
          : rows;
        setVenues(filtered);
      } catch (_) { setVenues([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open]);

  // Reset when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setQuery(''); setVenues([]); setSelected(null);
      setMessage(''); setProposedDate('');
    }
  }, [open]);

  const submit = async () => {
    if (!selected) { toast.error('Pick a venue first'); return; }
    if (message.length < 10) { toast.error('Message must be at least 10 characters'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/organizations/me/venue-invitations`,
        {
          venue_id: selected.venue_id,
          message,
          proposed_date: proposedDate || undefined,
        },
        auth(),
      );
      toast.success(`Invitation sent to ${selected.name}`);
      onSent && onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="invite-venue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-300" /> Invite a venue
          </DialogTitle>
          <DialogDescription>
            Reach out to a venue you'd like to host your event at. They'll get a
            notification and can accept directly from their dashboard.
          </DialogDescription>
        </DialogHeader>

        {!selected ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by venue name or city…"
                className="pl-10"
                data-testid="invite-venue-search"
              />
            </div>
            <div className="max-h-60 overflow-y-auto divide-y divide-border border border-border rounded-md">
              {venues.length === 0 ? (
                <p className="py-6 text-center text-xs text-muted-foreground">
                  {query ? 'No venues match.' : 'Start typing a venue name or city.'}
                </p>
              ) : (
                venues.map((v) => (
                  <button
                    key={v.venue_id}
                    type="button"
                    onClick={() => setSelected(v)}
                    className="block w-full text-left px-4 py-2.5 hover:bg-indigo-500/10 transition-colors"
                    data-testid={`invite-venue-pick-${v.venue_id}`}
                  >
                    <p className="font-semibold text-sm">{v.name}</p>
                    <p className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                      <MapPin className="h-3 w-3" /> {v.city}, {v.state}
                      {v.capacity_max ? ` · capacity ${v.capacity_max}` : ''}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Card className="border-indigo-500/30 bg-indigo-500/5">
              <CardContent className="p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{selected.name}</p>
                  <p className="text-[11px] text-muted-foreground">{selected.city}, {selected.state}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="text-xs text-indigo-300 hover:underline"
                  data-testid="invite-venue-change"
                >
                  Change
                </button>
              </CardContent>
            </Card>

            <label className="block">
              <span className="text-[11px] uppercase font-bold text-muted-foreground tracking-wide">
                Proposed date (optional)
              </span>
              <Input
                type="date"
                value={proposedDate}
                onChange={(e) => setProposedDate(e.target.value)}
                className="mt-1"
                data-testid="invite-proposed-date"
              />
            </label>

            <label className="block">
              <span className="text-[11px] uppercase font-bold text-muted-foreground tracking-wide">
                Message *
              </span>
              <Textarea
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell them about your event and why this venue would be a great fit…"
                className="mt-1"
                data-testid="invite-message"
              />
              <span className="text-[10px] text-muted-foreground">
                {message.length}/2000 — minimum 10 characters.
              </span>
            </label>

            <Button
              onClick={submit}
              disabled={submitting}
              className="w-full h-11 bg-gradient-to-r from-indigo-500 to-fuchsia-500 hover:from-indigo-400 hover:to-fuchsia-400 text-white font-bold"
              data-testid="invite-submit-btn"
            >
              {submitting
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                : <><Mail className="h-4 w-4 mr-2" /> Send invitation</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
